import { SeatDefinition, TripSeat } from '../types';

/**
 * Standardized Configurable 50-Passenger Coach Seating Configuration
 *
 * Physical Layout:
 * [السائق (غير متاح)]                         [باب الصعود]
 * [المقعد 1 (يسار شباك)] [المقعد 2 (يسار ممر)]  || ممر ||  [المقعد 3 (يمين ممر)] [المقعد 4 (يمين شباك)]
 * ... (الصفوف من 1 إلى 12: 48 مقعداً)
 * [المقعد 49 (كنبة أخيرة)]                    || ممر ||  [المقعد 50 (كنبة أخيرة)]
 * إجمالي السعة: 50 مقعداً
 */

export const TOTAL_VEHICLE_SEATS = 50;

export const generateBus50Seats = (): SeatDefinition[] => {
  const seats: SeatDefinition[] = [];
  let seatNum = 1;

  // 12 Rows x 4 seats = 48 seats
  for (let r = 1; r <= 12; r++) {
    const sectionName = r <= 4 ? 'FRONT_CAB' : r <= 8 ? 'ROW_1' : 'ROW_2';

    // Left Window (Col 0)
    seats.push({
      id: String(seatNum),
      seatNumber: seatNum,
      label: `المقعد ${seatNum} (صف ${r} - نافذة يسار)`,
      labelAr: `المقعد ${seatNum} (صف ${r} - نافذة يسار)`,
      row: r,
      column: 0,
      position: 'LEFT',
      section: sectionName as any,
      attribute: 'WINDOW',
      active: true,
    });
    seatNum++;

    // Left Aisle (Col 1)
    seats.push({
      id: String(seatNum),
      seatNumber: seatNum,
      label: `المقعد ${seatNum} (صف ${r} - ممر يسار)`,
      labelAr: `المقعد ${seatNum} (صف ${r} - ممر يسار)`,
      row: r,
      column: 1,
      position: 'CENTER',
      section: sectionName as any,
      attribute: 'MIDDLE',
      active: true,
    });
    seatNum++;

    // Right Aisle (Col 2)
    seats.push({
      id: String(seatNum),
      seatNumber: seatNum,
      label: `المقعد ${seatNum} (صف ${r} - ممر يمين)`,
      labelAr: `المقعد ${seatNum} (صف ${r} - ممر يمين)`,
      row: r,
      column: 2,
      position: 'CENTER',
      section: sectionName as any,
      attribute: 'MIDDLE',
      active: true,
    });
    seatNum++;

    // Right Window (Col 3)
    seats.push({
      id: String(seatNum),
      seatNumber: seatNum,
      label: `المقعد ${seatNum} (صف ${r} - نافذة يمين)`,
      labelAr: `المقعد ${seatNum} (صف ${r} - نافذة يمين)`,
      row: r,
      column: 3,
      position: 'RIGHT',
      section: sectionName as any,
      attribute: 'WINDOW',
      active: true,
    });
    seatNum++;
  }

  // Row 13: Rear row (seats 49 and 50)
  seats.push({
    id: '49',
    seatNumber: 49,
    label: 'المقعد 49 (الكنبة الأخيرة - يسار)',
    labelAr: 'المقعد 49 (الكنبة الأخيرة - يسار)',
    row: 13,
    column: 1,
    position: 'LEFT',
    section: 'BACK_ROW',
    attribute: 'MIDDLE',
    active: true,
  });

  seats.push({
    id: '50',
    seatNumber: 50,
    label: 'المقعد 50 (الكنبة الأخيرة - يمين)',
    labelAr: 'المقعد 50 (الكنبة الأخيرة - يمين)',
    row: 13,
    column: 2,
    position: 'RIGHT',
    section: 'BACK_ROW',
    attribute: 'MIDDLE',
    active: true,
  });

  return seats;
};

export const BUS_50_SEATS: SeatDefinition[] = generateBus50Seats();
export const TOYOTA_HIACE_14_SEATS: SeatDefinition[] = BUS_50_SEATS.slice(0, 14);

/**
 * Generates seat definitions dynamically matching the vehicle's exact capacity.
 */
export const generateSeatsForCapacity = (capacity: number = 50): SeatDefinition[] => {
  const cap = Math.max(1, capacity);
  if (cap <= 50) {
    return BUS_50_SEATS.slice(0, cap);
  }

  const seats = [...BUS_50_SEATS];
  for (let num = 51; num <= cap; num++) {
    const row = Math.ceil(num / 4);
    const col = (num - 1) % 4;
    seats.push({
      id: String(num),
      seatNumber: num,
      label: `المقعد ${num} (صف ${row})`,
      labelAr: `المقعد ${num} (صف ${row})`,
      row,
      column: col,
      position: col === 0 ? 'LEFT' : col === 3 ? 'RIGHT' : 'CENTER',
      section: `ROW_${row}`,
      attribute: col === 0 || col === 3 ? 'WINDOW' : 'MIDDLE',
      active: true,
    });
  }
  return seats;
};

/**
 * Returns a configured seat definition by number (1 to total capacity)
 */
export const getSeatDefinition = (seatNumber: number): SeatDefinition | undefined => {
  const predefined = BUS_50_SEATS.find((s) => s.seatNumber === seatNumber);
  if (predefined) return predefined;

  const row = Math.ceil(seatNumber / 4);
  const col = (seatNumber - 1) % 4;
  return {
    id: String(seatNumber),
    seatNumber,
    label: `المقعد ${seatNumber}`,
    labelAr: `المقعد ${seatNumber} (صف ${row})`,
    row,
    column: col,
    position: col === 0 ? 'LEFT' : col === 3 ? 'RIGHT' : 'CENTER',
    section: `ROW_${row}`,
    attribute: col === 0 || col === 3 ? 'WINDOW' : 'MIDDLE',
    active: true,
  };
};

/**
 * Automatically finds the next sequential available seat (1 to totalCapacity).
 * Scans starting from seat 1 upward.
 * If a seat is BOOKED or HELD by another student, it moves to the next.
 */
export const findNextSequentialSeat = (
  tripSeats: TripSeat[],
  totalCapacity: number = TOTAL_VEHICLE_SEATS,
  excludeSeatNumber?: number
): number | null => {
  const occupiedSet = new Set<number>();
  for (const s of tripSeats) {
    if (s.status === 'BOOKED' || s.status === 'HELD') {
      if (excludeSeatNumber !== undefined && s.seatNumber === excludeSeatNumber) {
        continue;
      }
      occupiedSet.add(s.seatNumber);
    }
  }

  for (let seatNum = 1; seatNum <= totalCapacity; seatNum++) {
    if (!occupiedSet.has(seatNum)) {
      return seatNum;
    }
  }

  return null; // Bus is full
};
